import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { productionLogger } from '@/utils/production-logger';
import { guardedSupabaseQuery } from '@/utils/supabaseGuard';

interface BackupStatus {
  lastBackup: string;
  nextBackup: string;
  healthStatus: 'healthy' | 'warning' | 'error';
  statusMessage: string;
}

export const BackupStatusWidget = () => {
  const [backupStatus, setBackupStatus] = useState<BackupStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBackupStatus = async () => {
      try {
        // Get REAL backup schedule data
        const scheduleResult = await guardedSupabaseQuery(
          async () => {
            return await supabase
              .from('backup_schedules')
              .select('*')
              .eq('name', 'Daily Automatic Backup (3am ET)')
              .single();
          },
          { 
            fallbackData: null,
            errorMessage: 'Unable to load backup schedule' 
          }
        );

        // Get REAL last backup completion from actual storage files
        const lastBackupResult = await guardedSupabaseQuery(
          async () => {
            return await supabase.rpc('get_latest_backup_timestamp');
          },
          { 
            fallbackData: null,
            errorMessage: 'Unable to load last backup timestamp' 
          }
        );

        if (scheduleResult.error && !scheduleResult.isOffline) {
          productionLogger.error('Error fetching schedule:', scheduleResult.error);
          setError('Failed to load backup schedule');
          return;
        }

        const now = new Date();
        let backupStatus: BackupStatus;

        if (scheduleResult.data) {
          // Use REAL schedule data
          const nextBackupTime = new Date(scheduleResult.data.next_run_at);
          let lastBackupTime: Date | null = null;
          let healthStatus: 'healthy' | 'warning' | 'error' = 'healthy';
          let statusMessage = 'All systems operational';

          // Use REAL last backup data from storage files
          if (lastBackupResult.data) {
            lastBackupTime = new Date(lastBackupResult.data);
            
            // Calculate hours since last backup
            const hoursSinceLastBackup = (now.getTime() - lastBackupTime.getTime()) / (1000 * 60 * 60);
            
            // Health check based on REAL 3 AM daily schedule (allow 28 hour window)
            if (hoursSinceLastBackup > 28) {
              healthStatus = 'error';
              statusMessage = 'Backup overdue';
            } else if (hoursSinceLastBackup > 25) {
              healthStatus = 'warning';
              statusMessage = 'Backup slightly delayed';
            }
          } else {
            // No backup files found
            healthStatus = 'error';
            statusMessage = 'No backup files found';
          }

          backupStatus = {
            lastBackup: lastBackupTime ? formatRelativeTime(lastBackupTime) : 'No backup files found',
            nextBackup: formatRelativeTime(nextBackupTime),
            healthStatus,
            statusMessage
          };
        } else {
          // No schedule found
          backupStatus = {
            lastBackup: 'Never',
            nextBackup: 'Not scheduled',
            healthStatus: 'error',
            statusMessage: 'Backup schedule not configured'
          };
        }

        setBackupStatus(backupStatus);
      } catch (error) {
        productionLogger.error('Error fetching backup status:', error);
        setError('Failed to load backup status');
      } finally {
        setLoading(false);
      }
    };

    fetchBackupStatus();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchBackupStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.abs(diffMs) / (1000 * 60 * 60);
    const diffDays = Math.abs(diffMs) / (1000 * 60 * 60 * 24);

    if (diffMs < 0) {
      // Past time
      if (diffHours < 1) {
        const minutes = Math.floor(diffHours * 60);
        return `${minutes} minutes ago`;
      } else if (diffHours < 48) { // Show hours up to 48 hours for better granularity
        return `${Math.floor(diffHours)} hours ago`;
      } else {
        return `${Math.floor(diffDays)} days ago`;
      }
    } else {
      // Future time
      if (diffHours < 1) {
        const minutes = Math.floor(diffHours * 60);
        return `in ${minutes} minutes`;
      } else if (diffHours < 48) { // Show hours up to 48 hours for better granularity
        return `in ${Math.floor(diffHours)} hours`;
      } else {
        return `in ${Math.floor(diffDays)} days`;
      }
    }
  };

  const getHealthIcon = () => {
    if (!backupStatus) return '⏳';
    
    switch (backupStatus.healthStatus) {
      case 'healthy': return '✅';
      case 'warning': return '⚠️';
      case 'error': return '🔴';
      default: return '⏳';
    }
  };

  const getHealthColor = () => {
    if (!backupStatus) return 'text-muted-foreground';
    
    switch (backupStatus.healthStatus) {
      case 'healthy': return 'text-primary';
      case 'warning': return 'text-accent';
      case 'error': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Backup Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="animate-pulse">
            <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Backup Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-destructive text-sm">
            <div className="flex items-center gap-2 mb-2">
              <span>🔴</span>
              <span>Error loading status</span>
            </div>
            <p className="text-xs text-muted-foreground">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Backup Status</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Last Backup:</span>
          <span className="font-medium">{backupStatus?.lastBackup || 'Unknown'}</span>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Next Backup:</span>
          <span className="font-medium">{backupStatus?.nextBackup || 'Unknown'}</span>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Health:</span>
          <div className="flex items-center gap-2">
            <span className={getHealthColor()}>{getHealthIcon()}</span>
            <span className={`font-medium ${getHealthColor()}`}>
              {backupStatus?.statusMessage || 'Unknown'}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};